<template>
  <div>
    <Head title="Monitor de Uso SaaS" />

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Monitor de Uso SaaS (Telemetría)</h1>
      </div>

      <!-- KPI Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="text-sm font-medium text-gray-500 truncate">Total Transacciones Procesadas</div>
          <div class="mt-1 text-3xl font-semibold text-gray-900">{{ kpis.totalTransactions.toLocaleString() }}</div>
        </div>
        <div class="bg-white rounded-lg shadow p-6">
          <div class="text-sm font-medium text-gray-500 truncate">Tasa de Éxito (30 días)</div>
          <div class="mt-1 text-3xl font-semibold text-gray-900">{{ kpis.successRate }}%</div>
        </div>
        <div class="bg-white rounded-lg shadow p-6">
          <div class="text-sm font-medium text-gray-500 truncate">Latencia Promedio</div>
          <div class="mt-1 text-3xl font-semibold text-gray-900">{{ kpis.avgLatency }} ms</div>
        </div>
        <div class="bg-white rounded-lg shadow p-6">
          <div class="text-sm font-medium text-gray-500 truncate">Cuentas Activas Hoy</div>
          <div class="mt-1 text-3xl font-semibold text-gray-900">{{ kpis.activeAccountsToday }}</div>
        </div>
      </div>

      <!-- Adopción por Cliente -->
      <div class="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 class="text-lg leading-6 font-medium text-gray-900">Adopción por Cliente</h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dominio / Cuenta</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operaciones Hoy</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operaciones Mes</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última Actividad</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Latencia Promedio</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(client, index) in clientAdoption" :key="index">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ client.domain }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ client.operations_today }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ client.operations_month }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ client.last_activity_human }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ client.avg_latency }} ms</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="client.status === 'Saludable' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ client.status }}
                  </span>
                </td>
              </tr>
              <tr v-if="clientAdoption.length === 0">
                <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">Sin datos de adopción.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Audit Log -->
      <div class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 class="text-lg leading-6 font-medium text-gray-900">Audit Log de Telemetría</h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Evento</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dominio Cliente</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Evento</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status HTTP</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha/Hora</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="movement in movements.data" :key="movement.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ movement.id }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ movement.client_domain }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-indigo-600 font-medium">{{ movement.event_type }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-md bg-gray-100 text-gray-800">
                    {{ movement.http_method }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="movement.status_code >= 200 && movement.status_code < 300 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ movement.status_code }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(movement.created_at) }}</td>
              </tr>
              <tr v-if="movements.data.length === 0">
                <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">No hay eventos registrados.</td>
              </tr>
            </tbody>
          </table>
          <div class="px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div class="flex items-center justify-between">
              <div class="flex-1 flex justify-between sm:hidden">
                <Link v-if="movements.prev_page_url" :href="movements.prev_page_url" class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"> Anterior </Link>
                <Link v-if="movements.next_page_url" :href="movements.next_page_url" class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"> Siguiente </Link>
              </div>
              <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p class="text-sm text-gray-700">
                    Mostrando <span class="font-medium">{{ movements.from || 0 }}</span> a <span class="font-medium">{{ movements.to || 0 }}</span> de <span class="font-medium">{{ movements.total }}</span> resultados
                  </p>
                </div>
                <div>
                  <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <template v-for="(link, key) in movements.links">
                      <div v-if="link.url === null" :key="key" class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 cursor-not-allowed" v-html="link.label" />
                      <Link v-else :key="'link-'+key" :href="link.url" :class="[link.active ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50', 'relative inline-flex items-center px-4 py-2 border text-sm font-medium']" v-html="link.label" />
                    </template>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  name: 'ExternalSalesMovementsIndex',

  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    movements: Object,
    kpis: {
      type: Object,
      default: () => ({
        totalTransactions: 0,
        successRate: 0,
        avgLatency: 0,
        activeAccountsToday: 0,
      }),
    },
    clientAdoption: {
      type: Array,
      default: () => [],
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    },
  },
}
</script>
